/* eslint-disable max-len */
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withModalActions, withUser } from 'core/hocs';
import { FormattedTag, RouteLink } from 'core/components';
import { isEmptyOrNil } from 'core/helpers';
import { USER_FIELDS, GAME_CATEGORY_FILTER } from 'core/constants';
import { IconProducers } from 'components/icons/icons-games-filters/icon-producers';
import { IconFavorite } from 'components/icons/icons-games-filters/icon-favorite';
import { IconSearch } from 'components/icons/icons-games-filters/icon-search';
import { IconArrowDown } from 'components/icons/icon-arrow-down/icon-arrow-down';
import { CATEGORY_IDS } from 'components/games-categories/games-categories';
import colors from 'customizations/js/color-variables';

import { GAMES_CATEGORIES_ICONS, CATEGORIES_ITEMS } from './games-categories-icons';
import { MODAL_IDS } from '../../constants';

const NAV_BAR_TEXT_CLASSNAME = 'text-small text-capitalize font-weight-semi-bold mt-0_75 letter-spacing-default';
const NAV_BAR_TEXT_CLASSNAME_DESKTOP = 'text-capitalize font-weight-semi-bold mt-1';

class GamesFiltersMenuUI extends PureComponent {
  static propTypes = {
    openModal: PropTypes.func.isRequired,
    currentModal: PropTypes.shape().isRequired,
    filterCategory: PropTypes.string,
    toggleProducers: PropTypes.func.isRequired,
    toggleSearchInput: PropTypes.func.isRequired,
    filterProducers: PropTypes.arrayOf(PropTypes.string).isRequired,
    isOpenSearch: PropTypes.bool,
    isUserLoggedIn: PropTypes.bool.isRequired,
    isStickyHeader: PropTypes.bool,
    className: PropTypes.string,
  };

  static defaultProps = {
    filterCategory: '',
    isOpenSearch: false,
    isStickyHeader: false,
    className: '',
  };

  openProducersModal = () => {
    const { openModal } = this.props;
    openModal(MODAL_IDS.PRODUCERS);
  };

  openGamesCategoriesModal = () => {
    const { openModal } = this.props;
    openModal(MODAL_IDS.GAMES_CATEGORIES);
  };

  render() {
    const {
      filterCategory,
      filterProducers,
      toggleProducers,
      toggleSearchInput,
      currentModal: { id },
      isOpenSearch,
      isUserLoggedIn,
      isStickyHeader,
      className,
    } = this.props;

    const IconCategory = GAMES_CATEGORIES_ICONS[filterCategory] || GAMES_CATEGORIES_ICONS[GAME_CATEGORY_FILTER.ALL];
    const isFavoriteFilterActive = filterCategory === GAME_CATEGORY_FILTER.FAVORITE;

    return (
      <>
        {/* MOBILE  */}
        <div
          className={classNames('games-filters-menu-mobile d-md-none icon-state-observer pt-0_25 px-1', {
            'logged-in': isUserLoggedIn,
            'border-bottom border-gray-10-20': !isStickyHeader
          }, className)}
        >
          <div
            role="button"
            tabIndex="0"
            onClick={this.openGamesCategoriesModal}
            onKeyPress={this.openGamesCategoriesModal}
            className="d-flex flex-column justify-content-center align-items-center cursor-pointer"
          >
            <div className="d-flex align-items-center ml-1_5">
              <IconCategory
                className={classNames('icon-category-small', {
                  active: !isFavoriteFilterActive,
                })}
              />

              <IconArrowDown
                className={classNames('ml-0_75', {
                  'icon-rotate': MODAL_IDS.GAMES_CATEGORIES === id,
                })}
              />
            </div>

            <FormattedTag
              id={CATEGORY_IDS[filterCategory] || CATEGORY_IDS[GAME_CATEGORY_FILTER.ALL]}
              className={classNames(NAV_BAR_TEXT_CLASSNAME, {
                'text-gray-20': isFavoriteFilterActive,
              })}
            />
          </div>

          <div
            role="button"
            tabIndex="0"
            onClick={this.openProducersModal}
            onKeyPress={this.openProducersModal}
            className="d-flex flex-column text-gray-20 justify-content-center align-items-center cursor-pointer"
          >
            <div className="d-flex align-items-center ml-1_5">
              <IconProducers />
              <IconArrowDown className={classNames('ml-0_75', { 'icon-rotate': MODAL_IDS.PRODUCERS === id })} />
            </div>

            <FormattedTag id="producers" className={NAV_BAR_TEXT_CLASSNAME} />
          </div>

          {isUserLoggedIn && (
            <RouteLink
              to={`/games/${GAME_CATEGORY_FILTER.FAVORITE.toLowerCase()}`}
              className="d-flex flex-column justify-content-center align-items-center cursor-pointer"
            >
              <IconFavorite
                className={classNames({
                  active: isFavoriteFilterActive,
                })}
              />

              <FormattedTag
                id="games.category.favorite"
                className={classNames(NAV_BAR_TEXT_CLASSNAME, {
                  active: isFavoriteFilterActive,
                  'text-gray-20': !isFavoriteFilterActive,
                })}
              />
            </RouteLink>
          )}

          <div
            role="button"
            tabIndex="0"
            onClick={toggleSearchInput}
            onKeyPress={toggleSearchInput}
            className="d-flex flex-column text-gray-20 justify-content-center align-items-center cursor-pointer"
          >
            <IconSearch />
            <FormattedTag id="search" className={NAV_BAR_TEXT_CLASSNAME} />
          </div>
        </div>

        {/* DESKTOP  */}
        <div className={classNames('games-filters-menu-desktop d-none d-md-flex icon-state-observer px-md-4 px-md-5', {
          'border-bottom border-gray-10-20': !isStickyHeader
        })}
        >
          <div className="games-filters-menu-desktop-items mr-md-3 mr-lg-10 w-75 d-flex justify-content-between">
            {CATEGORIES_ITEMS.map(({
              Icon,
              key,
              to,
              category,
              classname,
            }) => {
              const isActive = category === filterCategory;

              return (
                <RouteLink
                  key={key}
                  to={`/games/${to}`}
                  className={classNames(
                    'category-item d-flex flex-column align-items-center position-relative h-100 pt-2 mr-1 px-0_5',
                    { active: isActive },
                    classname
                  )}
                >
                  <Icon className={classNames({ active: isActive })} />

                  <FormattedTag
                    id={key}
                    className={classNames(
                      'category-name text-base text-capitalize font-weight-semi-bold pt-0_75 letter-spacing-default text-nowrap',
                      { 'text-gray-20': !isActive }
                    )}
                  />
                </RouteLink>
              );
            })}
          </div>

          <div className="ml-md-auto d-flex">
            <div
              role="button"
              tabIndex="0"
              onClick={toggleProducers}
              onKeyPress={toggleProducers}
              className="d-flex flex-column text-gray-20 justify-content-center align-items-center cursor-pointer mr-2_5"
            >
              <div className="d-flex align-items-center ml-1_5 text-capitalize">
                {!isEmptyOrNil(filterProducers) ? (
                  <span className="filter-producers bg-primary rounded text-white font-weight-semi-bold text-small d-flex justify-content-center align-items-center">
                    {filterProducers.length}
                  </span>
                ) : (
                  <IconProducers className="icon-producers-desktop" />
                )}

                <IconArrowDown color={colors.gray20} className={classNames('ml-0_5', { 'icon-rotate': MODAL_IDS.PRODUCERS === id })} />
              </div>
              <FormattedTag id="producers" className={classNames('category-name', NAV_BAR_TEXT_CLASSNAME_DESKTOP)} />
            </div>

            {isUserLoggedIn && (
              <RouteLink
                to={`/games/${GAME_CATEGORY_FILTER.FAVORITE.toLowerCase()}`}
                className="d-flex flex-column justify-content-center align-items-center cursor-pointer text-capitalize mr-2_5 without-underline-on-hover"
              >
                <IconFavorite
                  className={classNames('icon-favorite-desktop', {
                    active: isFavoriteFilterActive,
                  })}
                />

                <FormattedTag
                  id="games.category.favorite"
                  className={classNames('category-name text-base', NAV_BAR_TEXT_CLASSNAME_DESKTOP, {
                    'text-gray-20': !isFavoriteFilterActive,
                  })}
                />
              </RouteLink>
            )}

            <div
              role="button"
              tabIndex="0"
              onClick={toggleSearchInput}
              onKeyPress={toggleSearchInput}
              className={classNames('d-flex flex-column justify-content-center align-items-center cursor-pointer text-capitalize', {
                'text-gray-20': !isOpenSearch
              })}
            >
              <IconSearch color={isOpenSearch ? colors.primary : colors.gray20} className="icon-search-desktop" />
              <FormattedTag id="search" className={classNames('category-name', NAV_BAR_TEXT_CLASSNAME_DESKTOP)} />
            </div>
          </div>

        </div>
      </>
    );
  }
}

export const GamesFiltersMenu = withModalActions(
  withUser(
    GamesFiltersMenuUI, [
      USER_FIELDS.IS_USER_LOGGED_IN,
    ],
  )
);
